import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clapperboard,
  Download,
  LogOut,
  Search,
  Trash2,
  Users,
  TrendingUp,
  Tag,
  Loader2,
  Send,
  Mail,
  Megaphone,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SOURCE_COLORS = {
  Filmscape: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  Website: "bg-[#3B82F6]/15 text-[#93C5FD] border-[#3B82F6]/30",
  "QR Code": "bg-[#10B981]/15 text-[#6EE7B7] border-[#10B981]/30",
  Referral: "bg-[#A855F7]/15 text-[#D8B4FE] border-[#A855F7]/30",
  Other: "bg-[#64748B]/15 text-[#CBD5E1] border-[#64748B]/30",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#C5A059]/15 bg-[#0C172A] p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#94A3B8]">{label}</span>
        <Icon className="h-5 w-5 text-[#C5A059]" />
      </div>
      <div className="font-display text-3xl text-white mt-2">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [resending, setResending] = useState(null);
  const [bcOpen, setBcOpen] = useState(false);
  const [bcSubject, setBcSubject] = useState("");
  const [bcMessage, setBcMessage] = useState("");
  const [bcSending, setBcSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/crm/leads"), api.get("/crm/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchQ =
        !query ||
        `${l.first_name} ${l.last_name} ${l.email}`.toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "all" || l.career_interest === filter;
      return matchQ && matchF;
    });
  }, [leads, query, filter]);

  const interests = useMemo(
    () =>
      Array.from(new Set(leads.map((l) => l.career_interest).filter(Boolean))).sort(),
    [leads]
  );

  const onLogout = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  const onDelete = async (email) => {
    try {
      await api.delete(`/crm/leads/${encodeURIComponent(email)}`);
      setLeads((ls) => ls.filter((l) => l.email !== email));
      toast.success("Lead removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const onExport = () => {
    window.open(`${API}/crm/export`, "_blank");
  };

  const onResend = async (email) => {
    setResending(email);
    try {
      await api.post(`/crm/leads/${encodeURIComponent(email)}/resend`);
      toast.success(`Guide resent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setResending(null);
    }
  };

  const onBroadcast = async () => {
    if (!bcSubject.trim() || !bcMessage.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    setBcSending(true);
    try {
      const { data } = await api.post("/crm/broadcast", {
        subject: bcSubject,
        message: bcMessage,
      });
      toast.success(`Announcement sent to ${data.sent} of ${data.total} subscribers.`);
      setBcOpen(false);
      setBcSubject("");
      setBcMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setBcSending(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const renderLeadRow = (l) => (
    <TableRow
      key={l.email}
      className="border-[#C5A059]/10 hover:bg-[#12213D]/50"
      data-testid={`lead-row-${l.email}`}
    >
      <TableCell className="font-medium text-white">
        {l.first_name} {l.last_name}
      </TableCell>
      <TableCell className="text-[#94A3B8]">{l.email}</TableCell>
      <TableCell>
        {l.career_interest ? (
          <Badge variant="outline" className="bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/30">
            {l.career_interest}
          </Badge>
        ) : (
          <span className="text-[#475569]">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={SOURCE_COLORS[l.source] || SOURCE_COLORS.Other}>
          {l.source}
        </Badge>
      </TableCell>
      <TableCell className="text-[#94A3B8]">{fmtDate(l.created_at)}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onResend(l.email)}
            disabled={resending === l.email}
            title="Resend the Resource Guide"
            data-testid={`resend-${l.email}`}
            className="text-[#64748B] hover:text-[#C5A059] hover:bg-[#C5A059]/10"
          >
            {resending === l.email ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(l.email)}
            data-testid={`delete-${l.email}`}
            className="text-[#64748B] hover:text-[#E50914] hover:bg-[#E50914]/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderLeadsPanel = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20 text-[#C5A059]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      );
    }
    if (filtered.length === 0) {
      return (
        <div className="text-center py-20 text-[#94A3B8]" data-testid="empty-state">
          No leads found yet.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#C5A059]/15 hover:bg-transparent">
              <TableHead className="text-[#94A3B8]">Name</TableHead>
              <TableHead className="text-[#94A3B8]">Email</TableHead>
              <TableHead className="text-[#94A3B8]">Interest</TableHead>
              <TableHead className="text-[#94A3B8]">Source</TableHead>
              <TableHead className="text-[#94A3B8]">Signup Date</TableHead>
              <TableHead className="text-[#94A3B8] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{filtered.map(renderLeadRow)}</TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#060D18] text-white">
      <header className="border-b border-[#C5A059]/15 bg-[#0C172A] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clapperboard className="h-6 w-6 text-[#C5A059]" />
            <span className="font-display text-lg">CRM Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#94A3B8]">{user?.email}</span>
            <Button
              onClick={onLogout}
              variant="outline"
              data-testid="logout-btn"
              className="border-[#C5A059]/30 bg-transparent text-white hover:bg-[#C5A059]/10 hover:text-white rounded-full"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl">Leads &amp; Waitlist</h1>
            <p className="text-[#94A3B8] text-sm mt-1">Manage and export your collected leads.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setBcOpen(true)}
              data-testid="broadcast-open-btn"
              variant="outline"
              className="border-[#C5A059]/30 bg-transparent text-white hover:bg-[#C5A059]/10 hover:text-white rounded-full"
            >
              <Megaphone className="h-4 w-4 mr-2" /> Send Announcement
            </Button>
            <Button
              onClick={onExport}
              data-testid="export-csv-btn"
              className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Leads" value={stats.total} />
            <StatCard icon={TrendingUp} label="This Week" value={stats.this_week} />
            <StatCard
              icon={Tag}
              label="Top Interest"
              value={
                Object.entries(stats.by_interest).sort((a, b) => b[1] - a[1])[0]?.[0] || "—"
              }
            />
            <StatCard
              icon={Clapperboard}
              label="Sources"
              value={Object.keys(stats.by_source).length}
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
            <Input
              data-testid="search-input"
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 bg-[#0C172A] border-[#C5A059]/20 text-white h-11 focus-visible:ring-[#C5A059]"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              data-testid="filter-interest"
              className="w-full sm:w-56 bg-[#0C172A] border-[#C5A059]/20 text-white h-11"
            >
              <SelectValue placeholder="All Interests" />
            </SelectTrigger>
            <SelectContent className="bg-[#0C172A] border-[#C5A059]/20 text-white">
              <SelectItem value="all">All Interests</SelectItem>
              {interests.map((i) => (
                <SelectItem key={i} value={i}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-xl border border-[#C5A059]/15 bg-[#0C172A] overflow-hidden">
          {renderLeadsPanel()}
        </div>
      </main>

      <Dialog open={bcOpen} onOpenChange={setBcOpen}>
        <DialogContent className="bg-[#0C172A] border-[#C5A059]/20 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send Announcement</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Email all {leads.length} subscriber{leads.length === 1 ? "" : "s"} on the waitlist. Great for
              launch updates and news.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Subject</Label>
              <Input
                data-testid="broadcast-subject"
                value={bcSubject}
                onChange={(e) => setBcSubject(e.target.value)}
                placeholder="The book is launching soon! 🎬"
                className="bg-[#060D18] border-[#C5A059]/20 text-white h-11 focus-visible:ring-[#C5A059]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Message</Label>
              <Textarea
                data-testid="broadcast-message"
                value={bcMessage}
                onChange={(e) => setBcMessage(e.target.value)}
                rows={7}
                placeholder={"Write your update here.\n\nEach new line becomes a paragraph. A friendly greeting and sign-off are added automatically."}
                className="bg-[#060D18] border-[#C5A059]/20 text-white focus-visible:ring-[#C5A059] resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBcOpen(false)}
              className="border-[#C5A059]/30 bg-transparent text-white hover:bg-[#C5A059]/10 hover:text-white rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={onBroadcast}
              disabled={bcSending}
              data-testid="broadcast-send-btn"
              className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full"
            >
              {bcSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" /> Send to {leads.length}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
